import React, { useState } from 'react';
import { motion } from 'motion/react';
import { X, FileUp, AlertCircle } from 'lucide-react';
import Papa from 'papaparse';
import { cn } from '../../lib/utils';

interface EndGameModalProps {
  endGameStep: 'score' | 'pitch_counts' | 'game_ball' | null;
  setEndGameStep: (step: 'score' | 'pitch_counts' | 'game_ball' | null) => void;
  awayScore: string;
  setAwayScore: (score: string) => void;
  homeScore: string;
  setHomeScore: (score: string) => void;
  finalPitchCounts: Record<string, number>;
  setFinalPitchCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  isFinishingGame: boolean;
  setIsFinishingGame: (isFinishing: boolean) => void;
  handleFinishGame: (parsedData?: any[]) => void;
  saveError?: string | null;
  game: any;
  team: any;
  players: any[];
  catcherEvents?: { catcherId: string; type: 'PB' | 'BLOCK' }[];
  trackGameBalls?: boolean;
  selectedGameBallIds: string[];
  setSelectedGameBallIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const EndGameModal = ({
  endGameStep,
  setEndGameStep,
  awayScore,
  setAwayScore,
  homeScore,
  setHomeScore,
  finalPitchCounts,
  setFinalPitchCounts,
  isFinishingGame,
  setIsFinishingGame,
  handleFinishGame,
  saveError,
  game,
  team,
  players,
  catcherEvents = [],
  trackGameBalls,
  selectedGameBallIds,
  setSelectedGameBallIds,
}: EndGameModalProps) => {
  if (!endGameStep) return null;

  // Aggregate this game's passed balls / blocks per catcher for the recap.
  const catcherReport = (() => {
    const byCatcher: Record<string, { pb: number; block: number }> = {};
    for (const e of catcherEvents) {
      if (!e.catcherId) continue;
      const row = byCatcher[e.catcherId] || (byCatcher[e.catcherId] = { pb: 0, block: 0 });
      if (e.type === 'PB') row.pb++;
      else if (e.type === 'BLOCK') row.block++;
    }
    return Object.entries(byCatcher)
      .map(([pid, counts]) => ({ player: players.find((p: any) => p.id === pid), ...counts }))
      .filter(r => r.player);
  })();

  const titles: Record<string, string> = {
    score: 'Game Result',
    pitch_counts: 'Verify Pitch Counts',
    game_ball: 'Game Ball',
  };

  const toggleGameBall = (pid: string) => {
    setSelectedGameBallIds(prev =>
      prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]
    );
  };

  // Sort: unrecognized players first, then by name
  const sortedPlayers = [...players].sort((a, b) => {
    const aGB = a.seasonGameBalls ?? 0;
    const bGB = b.seasonGameBalls ?? 0;
    if (aGB !== bGB) return aGB - bGB;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-surface w-full max-w-md rounded-3xl p-6 border border-hairline"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-black italic uppercase">
            {titles[endGameStep] ?? endGameStep}
          </h3>
          <button onClick={() => setEndGameStep(null)} className="text-ink-2 hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>

        {endGameStep === 'score' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">
                  {game.isHome ? game.opponent : team.name} (Away)
                </label>
                <input
                  type="number"
                  value={awayScore}
                  onChange={(e) => setAwayScore(e.target.value)}
                  className="w-full bg-raised border border-hairline rounded-xl px-4 py-3 text-ink font-black text-2xl text-center outline-none focus:border-emerald-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">
                  {game.isHome ? team.name : game.opponent} (Home)
                </label>
                <input
                  type="number"
                  value={homeScore}
                  onChange={(e) => setHomeScore(e.target.value)}
                  className="w-full bg-raised border border-hairline rounded-xl px-4 py-3 text-ink font-black text-2xl text-center outline-none focus:border-emerald-500 transition-colors"
                />
              </div>
            </div>
            <button
              onClick={() => setEndGameStep('pitch_counts')}
              disabled={!awayScore || !homeScore}
              className="w-full py-4 bg-emerald-500 text-ink font-black rounded-xl uppercase italic hover:bg-emerald-400 transition-colors disabled:opacity-50"
            >
              Next: Verify Pitch Counts
            </button>
          </div>
        )}

        {endGameStep === 'pitch_counts' && (
          <div className="space-y-6">
            <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
              {Object.entries(finalPitchCounts).map(([pid, count]) => {
                const p = players.find((player: any) => player.id === pid);
                if (!p) return null;
                return (
                  <div key={pid} className="flex items-center justify-between bg-raised p-3 rounded-xl border border-hairline">
                    <span className="font-black uppercase text-sm">{p.name}</span>
                    <input
                      type="number"
                      value={count}
                      onChange={(e) => setFinalPitchCounts(prev => ({ ...prev, [pid]: parseInt(e.target.value) || 0 }))}
                      className="w-20 bg-surface border border-hairline rounded-lg px-3 py-2 text-ink font-black text-center outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                );
              })}
              {Object.keys(finalPitchCounts).length === 0 && (
                <p className="text-ink-2 text-sm text-center py-4">No pitches logged this game.</p>
              )}
            </div>
            <div className="pt-4 border-t border-hairline">
              <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">Add Pitcher</label>
              <select
                value=""
                onChange={(e) => {
                  const pid = e.target.value;
                  if (pid) {
                    setFinalPitchCounts(prev => ({ ...prev, [pid]: 0 }));
                  }
                }}
                className="w-full bg-raised border border-hairline rounded-xl px-4 py-2 text-ink font-black text-sm outline-none focus:border-emerald-500 transition-colors"
              >
                <option value="">Select player...</option>
                {players.filter((p: any) => !finalPitchCounts[p.id]).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {catcherReport.length > 0 && (
              <div className="pt-4 border-t border-hairline space-y-2">
                <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest">Catcher Report</label>
                {catcherReport.map(r => (
                  <div key={r.player.id} className="flex items-center justify-between bg-raised p-3 rounded-xl border border-hairline">
                    <span className="font-black uppercase text-sm">{r.player.name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-[11px] font-black uppercase tracking-widest text-accent-ink">{r.block} Blk</span>
                      <span className="text-[11px] font-black uppercase tracking-widest text-danger">{r.pb} PB</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {saveError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start space-x-3">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-black text-danger uppercase tracking-widest">Save Failed</p>
                  <p className="text-[10px] text-danger/70 mt-0.5">{saveError}</p>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                if (trackGameBalls) {
                  setEndGameStep('game_ball');
                } else {
                  handleFinishGame();
                }
              }}
              disabled={isFinishingGame && !saveError}
              className={`w-full py-4 font-black rounded-xl uppercase italic transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 ${saveError ? 'bg-red-500 text-ink hover:bg-red-400' : 'bg-emerald-500 text-ink hover:bg-emerald-400'}`}
            >
              {isFinishingGame && !saveError ? (
                <>
                  <div className="w-4 h-4 border-2 border-ink/20 border-t-ink rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{saveError ? 'Retry' : trackGameBalls ? 'Next: Game Ball' : 'Finish Game'}</span>
              )}
            </button>
          </div>
        )}

        {endGameStep === 'game_ball' && (
          <div className="space-y-5">
            <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest -mt-3">
              Select one or more players · optional
            </p>

            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {sortedPlayers.map((p: any) => {
                const selected = selectedGameBallIds.includes(p.id);
                const gbCount = p.seasonGameBalls ?? 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleGameBall(p.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors text-left',
                      selected
                        ? 'bg-amber-500/15 border-amber-500/40'
                        : 'bg-raised border-hairline hover:border-ink-mute'
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-7 h-7 bg-hairline rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                        #{p.number}
                      </div>
                      <span className={cn('font-black text-sm uppercase', selected ? 'text-gold-ink' : 'text-ink')}>
                        {p.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-[10px] font-bold text-ink-2 uppercase tracking-widest">
                        {gbCount === 0 ? 'none yet' : `${gbCount} this season`}
                      </span>
                      <div className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] transition-colors',
                        selected ? 'bg-amber-500 border-amber-500 text-ink' : 'border-track'
                      )}>
                        {selected && '✓'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pt-4 border-t border-hairline space-y-2">
              <button
                onClick={() => handleFinishGame()}
                disabled={isFinishingGame}
                className="w-full py-4 bg-amber-500 text-ink font-black rounded-xl uppercase italic hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isFinishingGame ? (
                  <>
                    <div className="w-4 h-4 border-2 border-ink/20 border-t-ink rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>{selectedGameBallIds.length > 0 ? `Award Game Ball${selectedGameBallIds.length > 1 ? 's' : ''}` : 'Finish Game'}</span>
                )}
              </button>
              {selectedGameBallIds.length === 0 && (
                <p className="text-center text-[10px] text-ink-2 font-bold uppercase tracking-widest">
                  No selection = no game ball awarded
                </p>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
